'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { noto_sans } from '@/globalConstants';
import { useAppContext } from '@/contexts/AppContext';

// `href` is where the link goes; `match` is the section it stands for, which is not
// always the same route — Panchanga points at /panchanga but represents all of
// /panchanga. `exact` is for Home, since every route sits under '/' and it would
// otherwise light up everywhere.
type NavLink = { label: string; href: string; match: string; exact?: boolean };

const NAV_LINKS: NavLink[] = [
  { label: 'Home', href: '/', match: '/', exact: true },
  { label: 'Panchanga', href: '/panchanga', match: '/panchanga' },
  { label: 'Prarthana', href: '/prarthana', match: '/prarthana' },
  { label: 'Festivals', href: '/festivals', match: '/festivals' },
  { label: 'Shubh Dina', href: '/shubh-dina', match: '/shubh-dina' },
  { label: 'Rashifal', href: '/rashifal', match: '/rashifal' },
  // /articles is the listing; a single piece lives under /articles/<slug>, so the
  // section highlight follows the visitor into the article they opened.
  { label: 'Articles', href: '/articles', match: '/articles' },
  { label: 'About Us', href: '/about-us', match: '/about-us' },
];

// Trailing slashes, query strings and hashes are noise when comparing routes
// ('/festivals/' and '/festivals' are the same page).
const normalizePath = (path: string) => {
  const base = path.split('?')[0].split('#')[0];
  return base.length > 1 ? base.replace(/\/+$/, '') : '/';
};

// Active = the visitor is somewhere in this section, i.e. on its own page or any page
// beneath it. This drives the highlight only; whether the link is clickable is a
// separate, stricter test (exact page match).
const isSectionActive = (link: NavLink, currentPath: string) => {
  const section = normalizePath(link.match);
  if (link.exact) return currentPath === section;
  return currentPath === section || currentPath.startsWith(`${section}/`);
};

// Same destinations and icons the Footer links to, so the two stay in step.
const SOCIAL_LINKS = [
  { href: 'https://www.instagram.com/dharmayana.app/', icon: '/instagram-new-icon.svg', title: 'Follow us on Instagram' },
  { href: 'https://www.linkedin.com/company/oit-innovations-pvt-ltd/', icon: '/linkedin-new-icon.svg', title: 'Follow us on Linkedin' },
  { href: 'https://www.youtube.com/@Dharmayana_app', icon: '/youtube-new-icon.svg', title: 'Subscribe to our YouTube Channel' },
  { href: 'https://www.facebook.com/profile.php?id=61578439461358', icon: '/facebook-new-icon.svg', title: 'Follow our Facebook Page' },
];

// Past this many pixels the scroll-only variant slides its bar into view — far enough
// down that the bar doesn't flicker in and out on a nudge of the wheel.
const REVEAL_AT = 90;

// The hero variant collapses on the first pixel of scroll instead. Its expanded form is
// tall and transparent, and the header is fixed, so the moment the page moves the hero
// heading slides up underneath the link row and the two are drawn on top of each other.
// Nothing here waits for a threshold the overlap could fit inside; a couple of pixels of
// slack keeps a rubber-band bounce at the top from toggling it.
const COLLAPSE_AT = 2;

// Height of the scalloped edge below the bar (its h-6).
const WAVE_HEIGHT = 24;

// 'hero'        — the homepage: expanded (centred logo over a full-width link row) at
//                 the top of the hero, collapsing to the compact bar on scroll.
// 'scroll-only' — pages that open with their own PageBanner logo: nothing at the top,
//                 just the compact bar sliding in once scrolled.
// 'compact'     — pages with no banner artwork (the articles section): the compact bar
//                 from the first pixel, with a spacer below it so it never covers the
//                 top of the page.
export type SiteHeaderVariant = 'hero' | 'scroll-only' | 'compact';

const SiteHeader = ({ variant = 'hero' }: { variant?: SiteHeaderVariant }) => {
  const { isAppView } = useAppContext();
  const pathname = usePathname();
  const currentPath = normalizePath(pathname || '/');
  const isHero = variant === 'hero';
  const isScrollOnly = variant === 'scroll-only';
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isPastHeaderImage, setIsPastHeaderImage] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    // Resolved per route: each page marks its own hero/banner, and this component stays
    // mounted across client-side navigations.
    const headerImage = document.querySelector('[data-header-image]');

    const onScroll = () => {
      setIsCollapsed(window.scrollY > (isHero ? COLLAPSE_AT : REVEAL_AT));
      // A page with no marked artwork (plain content pages) has the bar sitting over
      // body content from the outset, so the scalloped edge applies straight away.
      setIsPastHeaderImage(!headerImage || headerImage.getBoundingClientRect().bottom <= 0);
    };

    onScroll(); // honour a restored scroll position on load
    window.addEventListener('scroll', onScroll, { passive: true });
    // The homepage hero is sized in vh, so its bottom edge moves when the window does.
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [pathname, isHero]);

  const closeMenu = useCallback(() => setIsMenuOpen(false), []);

  // The compact variant is fixed over a page that has no banner to sit on, so it
  // reserves its own height in the flow, plus the scalloped edge hanging below it. Measured from whichever bar is showing (the
  // other is display:none and reads 0) rather than hard-coded, so it follows the
  // logo's breakpoint sizes. The menu panel is left out: it should overlay the page
  // when open, not push it down.
  const mobileBarRef = useRef<HTMLDivElement>(null);
  const desktopBarRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState<number | null>(null);
  useEffect(() => {
    if (variant !== 'compact') return;
    const bars = [mobileBarRef.current, desktopBarRef.current].filter(Boolean) as HTMLDivElement[];
    const measure = () => setBarHeight(Math.max(...bars.map((bar) => bar.offsetHeight)) + WAVE_HEIGHT);
    measure();
    const observer = new ResizeObserver(measure);
    bars.forEach((bar) => observer.observe(bar));
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [variant]);

  // Hold the page still behind the open menu, and let Escape dismiss it.
  useEffect(() => {
    if (!isMenuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen, closeMenu]);

  // Only the hero variant has an expanded form; only the scroll-only one stays
  // off-screen until scrolled.
  const isCompact = !isHero || isCollapsed;
  const isHidden = isScrollOnly && !isCollapsed;
  // Solid once compact or while the mobile menu is open; otherwise the header sits
  // transparently on the hero artwork.
  const isSolid = isCompact || isMenuOpen;
  // The scalloped edge only makes sense once the bar is a solid band floating over
  // ordinary page content — not while it is transparent on the hero artwork.
  const showWave = isSolid && isPastHeaderImage;
  // The compact bar while it is still over the hero/banner artwork: no scalloped edge
  // to give it weight yet, so it carries extra height instead. Once the wave takes
  // over (past the artwork) the bar returns to its normal, tighter height.
  const isOverHeaderImage = isCompact && !isPastHeaderImage;

  // Scrolling back to the top of a subpage takes the bar away with the menu still open,
  // which would leave an invisible open menu behind.
  useEffect(() => {
    if (isHidden) setIsMenuOpen(false);
  }, [isHidden]);

  // Rendered identically in the desktop row and the mobile panel, so the highlight and
  // the "you are here" rule can't drift apart between the two.
  const navItems = (onNavigate?: () => void) =>
    NAV_LINKS.map((link) => {
      const active = isSectionActive(link, currentPath);
      // Only the exact page is unclickable: on /festivals/ganesha-chaturthi the
      // Festivals link is highlighted but still navigates to /festivals.
      const isCurrentPage = currentPath === normalizePath(link.href);

      return (
        <li
          key={link.label}
          className={`whitespace-nowrap transition-colors delay-100 duration-200 ease-in-out ${
            active ? 'text-[#ebe793]' : 'text-white hover:text-[#ebe793]'
          }`}
        >
          {isCurrentPage ? (
            // A span rather than a disabled link: there is nowhere to navigate to, so it
            // stays out of the tab order and announces itself as the current page.
            <span aria-current="page" className="cursor-default">
              {link.label}
            </span>
          ) : (
            <Link href={link.href} onClick={onNavigate} className="cursor-pointer">
              {link.label}
            </Link>
          )}
        </li>
      );
    });

  const logo = (
    <Link href="/" aria-label="Dharmayana home" onClick={closeMenu}>
      <Image
        src="/updated-logo.svg"
        alt="Dharmayana"
        height={48}
        width={210}
        loading="eager"
        // The wordmark is wide for its height (~7:1), so in the compact bar it takes a
        // step down between lg and xl — that is where the desktop row first appears and
        // the eight links leave it nothing to share the line with.
        className={`w-auto transition-all duration-300 ${
          isCompact ? 'h-9 md:h-10 lg:h-8 xl:h-10' : 'h-10 md:h-12'
        }`}
      />
    </Link>
  );

  // The native app renders these screens in a webview and supplies its own chrome.
  if (isAppView) return null;

  return (
    <>
      <header
        // Above all page content (which tops out around z-50) but below MUI's modals and
        // poppers (1300/1200) and the bottom app-install nudge, so dialogs and date
        // pickers still overlay the bar as they should.
        className={`fixed inset-x-0 top-0 z-[900] ${noto_sans.className} transition-all duration-300 ${
          isSolid ? 'bg-[#05574E]' : 'bg-transparent'
        } ${
          showWave ? '' : isSolid ? 'shadow-[0_2px_16px_rgba(0,0,0,0.18)]' : ''
        } ${isHidden ? '-translate-y-full opacity-0 invisible' : 'translate-y-0 opacity-100 visible'}`}
      >
        {/* ---------- Mobile / tablet bar ---------- */}
        <div
          ref={mobileBarRef}
          className={`lg:hidden flex items-center justify-between w-[92%] mx-auto transition-all duration-300 ${
            isOverHeaderImage ? 'py-7' : 'py-4'
          }`}
        >
          {logo}
          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="home-mobile-menu"
            className="grid gap-[5px] p-2 -mr-2"
          >
            <span
              className={`block w-6 h-[2px] rounded bg-white transition-transform duration-300 ${
                isMenuOpen ? 'translate-y-[7px] rotate-45' : ''
              }`}
            />
            <span
              className={`block w-6 h-[2px] rounded bg-white transition-opacity duration-300 ${
                isMenuOpen ? 'opacity-0' : 'opacity-100'
              }`}
            />
            <span
              className={`block w-6 h-[2px] rounded bg-white transition-transform duration-300 ${
                isMenuOpen ? '-translate-y-[7px] -rotate-45' : ''
              }`}
            />
          </button>
        </div>

        {/* ---------- Desktop ----------
            One element in both states: expanded it stacks a centred logo over a
            full-width row of links; collapsed it becomes a single row with the logo
            on the left. Keeping it as one tree (rather than cross-fading two) means
            the links never duplicate in the accessibility tree. */}
        <div
          ref={desktopBarRef}
          className={`hidden lg:flex mx-auto transition-all duration-300 ${
            isCompact
              ? // Full-bleed with a small inset so the logo sits close to the screen edge.
                // No justify-between here: the nav's auto side margins (below) share the
                // leftover width evenly instead of stacking it all before the links.
                `w-full px-6 2xl:px-10 flex-row items-center ${
                  isOverHeaderImage ? 'py-6' : 'py-3'
                }`
              : // px-24 mirrors HeroSection's own lg:px-24, so the first link lines up
                // vertically with "Your" in the hero heading and the last one with its
                // right edge.
                'w-full px-24 flex-col items-stretch gap-20 xl:gap-20 pt-7 pb-6'
          }`}
        >
          <div className={isCompact ? '' : 'flex justify-center'}>{logo}</div>
          <nav aria-label="Primary" className={isCompact ? 'mx-auto' : ''}>
            <ul
              // Compact: the row is logo + links on one line, so the gaps step down with the
              // viewport — eight links at gap-12 leave barely a dozen pixels beside the logo
              // at 1024px. Expanded: justify-between spreads them across the hero's own
              // width, and the gap falls out of that.
              className={`flex items-center font-semibold text-[1rem] tracking-[0.02em] ${
                isCompact ? 'gap-4 xl:gap-8 2xl:gap-12' : 'justify-between w-full'
              }`}
            >
              {navItems()}
            </ul>
          </nav>
        </div>

        {/* ---------- Mobile menu panel ---------- */}
        <div
          id="home-mobile-menu"
          className={`lg:hidden overflow-hidden bg-[#05574E] transition-[max-height,opacity] duration-300 ease-in-out ${
            isMenuOpen ? 'max-h-[80vh] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0'
          }`}
        >
          <nav aria-label="Primary" className="pb-8 pt-2">
            <ul className="flex flex-col items-center gap-6 font-semibold text-[1.05rem]">
              {navItems(closeMenu)}
            </ul>

            <p className="text-[#B7C988] font-bold text-sm tracking-[0.08em] text-center mt-10 mb-5">
              SOCIAL LINKS
            </p>
            <ul className="flex justify-center gap-5">
              {SOCIAL_LINKS.map(({ href, icon, title }) => (
                <li key={href} className="bg-white rounded-full p-2">
                  <Link href={href} title={title} target="_blank" rel="noopener noreferrer">
                    <Image src={icon} alt={title} width={22} height={22} style={{ width: 'auto', height: 'auto' }} />
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Scalloped bottom edge. Sits just below the bar (top-full) so it adds no height,
            and the tile's own solid top band continues the bar's green before dripping.
            24px tall with nothing behind it, so page content shows through the gaps. */}
        {showWave && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-full h-6 bg-repeat-x bg-top"
            style={{ backgroundImage: "url('/bottom-wave.svg')", backgroundSize: '107px 24px' }}
          />
        )}
      </header>
      {variant === 'compact' && (
        // Until measured (server render, first paint), the classes approximate bar + wave:
        // py-4 around the h-9/md:h-10 logo on mobile, py-3 around lg:h-8/xl:h-10 above.
        <div
          aria-hidden="true"
          className="h-[92px] md:h-[96px] lg:h-20 xl:h-[88px]"
          style={barHeight ? { height: barHeight } : undefined}
        />
      )}
    </>
  );
};

export default SiteHeader;
